import type { DeviceDto } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";

function readMetaString(meta: JsonObject | null | undefined, key: string): string {
  const value = meta?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function readMetaObject(meta: JsonObject | null | undefined, key: string): JsonObject | null {
  const value = meta?.[key];
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as JsonObject;
  }
  return null;
}

function titleCaseWords(value: string): string {
  return value
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatMetadataDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString();
}

export function getDeviceSerialNumber(device: DeviceDto): string {
  const meta = device.metadata as JsonObject | null | undefined;
  const provisioning = readMetaObject(meta, "provisioning");
  return (
    readMetaString(provisioning, "serial_number") ||
    readMetaString(provisioning, "serialNumber") ||
    readMetaString(meta, "serial_number") ||
    readMetaString(meta, "serialNumber")
  );
}

export function getDeviceSecondaryIdentifier(device: DeviceDto): string {
  return getDeviceSerialNumber(device) || device.device_uid;
}

export function getDevicePhotoPath(device: DeviceDto): string | null {
  const meta = device.metadata as JsonObject | null | undefined;
  const path = readMetaString(meta, "device_photo_path");
  return path || null;
}

/** Protocol / uplink label from device metadata (commissioning model). */
export function getDeviceProtocolLabel(device: DeviceDto): string {
  const meta = device.metadata as JsonObject | null | undefined;
  const raw = readMetaString(meta, "transmission_mode") || readMetaString(meta, "protocol");
  if (!raw) {
    return "--";
  }
  return titleCaseWords(raw.replace(/_/g, " "));
}

export function getDeviceLocationLabel(device: DeviceDto): {
  primary: string;
  secondary: string;
  title: string;
} {
  const meta = device.metadata as JsonObject | null | undefined;
  const basic = readMetaObject(meta, "basic");
  const location =
    readMetaString(basic, "location") || readMetaString(meta, "site") || readMetaString(meta, "location") || "--";
  const city = readMetaString(basic, "city") || readMetaString(meta, "city");
  const stateId = readMetaString(basic, "state_id") || readMetaString(meta, "state_id");
  const state = stateId ? readMetaString(basic, "state") || readMetaString(meta, "state") : "";
  const region = [city, state].filter(Boolean).join(", ");

  return {
    primary: location,
    secondary: region,
    title: region ? `${location} | ${region}` : location,
  };
}

export function getDeviceSiteLabel(device: DeviceDto): string {
  return getDeviceLocationLabel(device).primary;
}

export function getDeviceLifecycleDates(device: DeviceDto): Array<{ label: string; value: string }> {
  const meta = device.metadata as JsonObject | null | undefined;
  const items = [
    { label: "Install", raw: readMetaString(meta, "installation_date") },
    { label: "Warranty", raw: readMetaString(meta, "warranty_end") },
    { label: "Calibration", raw: readMetaString(meta, "next_calibration") },
  ];

  return items
    .filter((item) => item.raw)
    .map((item) => ({
      label: item.label,
      value: formatMetadataDate(item.raw),
    }));
}

export function getDeviceAssignedUsers(device: DeviceDto): string[] {
  if (device.assigned_users?.length) {
    return device.assigned_users.map((user) => user.name || user.email);
  }
  const meta = device.metadata as JsonObject | null | undefined;
  const candidates = [
    meta?.assigned_users,
    meta?.assignedUsers,
    meta?.assignees,
    meta?.assigned_to,
    meta?.assignedTo,
    meta?.owners,
    meta?.contacts,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const values = candidate
        .map((entry) => {
          if (typeof entry === "string" && entry.trim()) {
            return entry.trim();
          }
          if (entry && typeof entry === "object" && !Array.isArray(entry)) {
            const item = entry as JsonObject;
            const name = readMetaString(item, "name");
            const email = readMetaString(item, "email");
            return name || email;
          }
          return "";
        })
        .filter(Boolean);
      if (values.length > 0) {
        return values;
      }
    }

    if (typeof candidate === "string" && candidate.trim()) {
      return candidate
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
}

export function getDeviceSamplingLabel(device: DeviceDto): { primary: string; secondary: string } {
  const meta = device.metadata as JsonObject | null | undefined;
  const hardwareMeta = readMetaObject(meta, "hardware");

  const samplingRateValue = hardwareMeta?.samplingRateValue;
  const samplingRatePeriod = hardwareMeta?.samplingRatePeriod;

  const value =
    typeof samplingRateValue === "number"
      ? samplingRateValue
      : typeof samplingRateValue === "string" && samplingRateValue.trim()
        ? Number(samplingRateValue)
        : null;
  const period =
    samplingRatePeriod === "second" ||
    samplingRatePeriod === "minute" ||
    samplingRatePeriod === "hour" ||
    samplingRatePeriod === "day"
      ? samplingRatePeriod
      : null;

  if (value != null && Number.isFinite(value) && period) {
    return {
      primary: `${value} sample${value === 1 ? "" : "s"}`,
      secondary: `Per ${period}`,
    };
  }

  return {
    primary: "Not set",
    secondary: "No data",
  };
}

export function getDeviceTags(device: DeviceDto): string[] {
  const meta = device.metadata as JsonObject | null | undefined;
  const tags = meta?.tags;
  if (!Array.isArray(tags)) {
    return [];
  }
  return tags.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

export function formatDeviceLastSeen(iso: string | null | undefined): string {
  if (!iso) {
    return "--";
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleString();
}
